import { AuthToken } from '../value-objects/AuthToken';
import { User } from '../models/User';
import { Session } from '../models/Session';
import { LoginAttempt } from '../value-objects/LoginAttempt';
import { UserId } from '../value-objects/UserId';
import { SessionId } from '../value-objects/SessionId';

/**
 * 로컬 스토리지 키 상수
 */
export const AUTH_STORAGE_KEYS = {
  ACCESS_TOKEN: 'auth_access_token',
  REFRESH_TOKEN: 'auth_refresh_token',
  TOKEN_EXPIRES_AT: 'auth_token_expires_at',
  USER_DATA: 'auth_user_data',
  SESSION_DATA: 'auth_session_data',
  LOGIN_ATTEMPTS: 'auth_login_attempts',
  SECURITY_SETTINGS: 'auth_security_settings',
  LAST_ACTIVITY: 'auth_last_activity',
} as const;

/**
 * 로컬 스토리지 기반 인증 데이터 어댑터
 * 브라우저 로컬 스토리지를 사용한 인증 정보 관리
 */
export class AuthLocalStorageAdapter {
  /**
   * 토큰 저장
   */
  static saveToken(token: AuthToken): void {
    try {
      localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, token.accessToken);
      localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, token.refreshToken);
      localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN_EXPIRES_AT, token.expiresAt.toISOString());
    } catch (error) {
      console.error('토큰 저장 실패:', error);
    }
  }

  /**
   * 토큰 불러오기
   * [2026-01-05] 만료된 토큰도 삭제하지 않음 - refresh token으로 갱신 시도 가능하도록
   */
  static loadToken(): AuthToken | null {
    try {
      const accessToken = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
      const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
      const expiresAtStr = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN_EXPIRES_AT);

      if (!accessToken || !refreshToken || !expiresAtStr) {
        return null;
      }

      const expiresAt = new Date(expiresAtStr);

      // [2026-01-05] 만료되어도 토큰 객체 반환 (삭제하지 않음)
      // 호출자가 isExpired()로 확인 후 갱신 시도 가능
      // 갱신 실패 시에만 clearAll() 호출하여 정리

      return new AuthToken({
        accessToken,
        refreshToken,
        expiresAt,
      });
    } catch (error) {
      console.error('토큰 불러오기 실패:', error);
      // 파싱 오류 시에만 정리
      AuthLocalStorageAdapter.removeToken();
      return null;
    }
  }

  /**
   * Refresh Token만 가져오기 (갱신용)
   */
  static getRefreshToken(): string | null {
    return localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * 토큰 삭제
   */
  static removeToken(): void {
    try {
      localStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN_EXPIRES_AT);
    } catch (error) {
      console.error('토큰 삭제 실패:', error);
    }
  }

  /**
   * 사용자 데이터 저장
   */
  static saveUser(user: User): void {
    try {
      const userData = {
        id: user.id.value,
        email: user.email.value,
        phone: user.phone.value,
        name: user.name,
        role: user.role.value,
        isActive: user.isActive,
        lastLoginAt: user.lastLoginAt?.toISOString(),
        profileImageUrl: user.profileImageUrl,
        accountLockedUntil: user.accountLockedUntil?.toISOString(),
        passwordChangedAt: user.passwordChangedAt?.toISOString(),
      };
      localStorage.setItem(AUTH_STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
    } catch (error) {
      console.error('사용자 데이터 저장 실패:', error);
    }
  }

  /**
   * 사용자 데이터 불러오기
   */
  static loadUser(): any | null {
    try {
      const userDataStr = localStorage.getItem(AUTH_STORAGE_KEYS.USER_DATA);
      if (!userDataStr) {
        return null;
      }
      return JSON.parse(userDataStr);
    } catch (error) {
      console.error('사용자 데이터 불러오기 실패:', error);
      return null;
    }
  }

  /**
   * 사용자 데이터 삭제
   */
  static removeUser(): void {
    try {
      localStorage.removeItem(AUTH_STORAGE_KEYS.USER_DATA);
    } catch (error) {
      console.error('사용자 데이터 삭제 실패:', error);
    }
  }

  /**
   * 세션 데이터 저장
   */
  static saveSession(session: Session): void {
    try {
      const sessionData = {
        id: session.id.value,
        userId: session.userId.value,
        ipAddress: session.ipAddress,
        userAgent: session.userAgent,
        expiresAt: session.expiresAt?.toISOString(),
        lastAccessedAt: session.lastAccessedAt?.toISOString(),
        createdAt: session.createdAt.toISOString(),
        updatedAt: session.updatedAt.toISOString(),
      };
      localStorage.setItem(AUTH_STORAGE_KEYS.SESSION_DATA, JSON.stringify(sessionData));
    } catch (error) {
      console.error('세션 데이터 저장 실패:', error);
    }
  }

  /**
   * 세션 데이터 불러오기
   */
  static loadSession(): any | null {
    try {
      const sessionDataStr = localStorage.getItem(AUTH_STORAGE_KEYS.SESSION_DATA);
      if (!sessionDataStr) {
        return null;
      }
      return JSON.parse(sessionDataStr);
    } catch (error) {
      console.error('세션 데이터 불러오기 실패:', error);
      return null;
    }
  }

  /**
   * 세션 데이터 삭제
   */
  static removeSession(): void {
    try {
      localStorage.removeItem(AUTH_STORAGE_KEYS.SESSION_DATA);
    } catch (error) {
      console.error('세션 데이터 삭제 실패:', error);
    }
  }

  /**
   * 로그인 시도 기록 저장
   */
  static saveLoginAttempt(attempt: LoginAttempt): void {
    try {
      const attempts = this.loadLoginAttempts();
      const newAttempt = {
        email: attempt.email,
        timestamp: attempt.timestamp.toISOString(),
        success: attempt.success,
        ipAddress: attempt.ipAddress,
        userAgent: attempt.userAgent,
      };
      
      attempts.push(newAttempt);
      
      // 최근 100개만 유지
      const recentAttempts = attempts
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 100);
      
      localStorage.setItem(AUTH_STORAGE_KEYS.LOGIN_ATTEMPTS, JSON.stringify(recentAttempts));
    } catch (error) {
      console.error('로그인 시도 기록 저장 실패:', error);
    }
  }

  /**
   * 로그인 시도 기록 불러오기
   */
  static loadLoginAttempts(): any[] {
    try {
      const attemptsStr = localStorage.getItem(AUTH_STORAGE_KEYS.LOGIN_ATTEMPTS);
      if (!attemptsStr) {
        return [];
      }
      return JSON.parse(attemptsStr);
    } catch (error) {
      console.error('로그인 시도 기록 불러오기 실패:', error);
      return [];
    }
  }

  /**
   * 로그인 시도 기록 삭제
   */
  static removeLoginAttempts(): void {
    try {
      localStorage.removeItem(AUTH_STORAGE_KEYS.LOGIN_ATTEMPTS);
    } catch (error) {
      console.error('로그인 시도 기록 삭제 실패:', error);
    }
  }

  /**
   * 보안 설정 저장
   */
  static saveSecuritySettings(settings: {
    rememberMe: boolean;
    autoLogoutMinutes: number;
    twoFactorEnabled: boolean;
    allowMultipleSessions: boolean;
  }): void {
    try {
      localStorage.setItem(AUTH_STORAGE_KEYS.SECURITY_SETTINGS, JSON.stringify(settings));
    } catch (error) {
      console.error('보안 설정 저장 실패:', error);
    }
  }

  /**
   * 보안 설정 불러오기
   */
  static loadSecuritySettings(): {
    rememberMe: boolean;
    autoLogoutMinutes: number;
    twoFactorEnabled: boolean;
    allowMultipleSessions: boolean;
  } {
    try {
      const settingsStr = localStorage.getItem(AUTH_STORAGE_KEYS.SECURITY_SETTINGS);
      if (!settingsStr) {
        return {
          rememberMe: false,
          autoLogoutMinutes: 360,  // 6시간 (2026-01-05)
          twoFactorEnabled: false,
          allowMultipleSessions: false,
        };
      }
      return JSON.parse(settingsStr);
    } catch (error) {
      console.error('보안 설정 불러오기 실패:', error);
      return {
        rememberMe: false,
        autoLogoutMinutes: 360,  // 6시간 (2026-01-05)
        twoFactorEnabled: false,
        allowMultipleSessions: false,
      };
    }
  }

  /**
   * 마지막 활동 시간 업데이트
   */
  static updateLastActivity(): void {
    try {
      localStorage.setItem(AUTH_STORAGE_KEYS.LAST_ACTIVITY, new Date().toISOString());
    } catch (error) {
      console.error('마지막 활동 시간 업데이트 실패:', error);
    }
  }

  /**
   * 마지막 활동 시간 불러오기
   */
  static getLastActivity(): Date | null {
    try {
      const lastActivityStr = localStorage.getItem(AUTH_STORAGE_KEYS.LAST_ACTIVITY);
      if (!lastActivityStr) {
        return null;
      }
      return new Date(lastActivityStr);
    } catch (error) {
      console.error('마지막 활동 시간 불러오기 실패:', error);
      return null;
    }
  }

  /**
   * 비활성 시간 계산 (분)
   */
  static getInactiveMinutes(): number {
    const lastActivity = this.getLastActivity();
    if (!lastActivity) {
      return 0;
    }
    
    const now = new Date();
    const diffMs = now.getTime() - lastActivity.getTime();
    return Math.floor(diffMs / (1000 * 60));
  }

  /**
   * 자동 로그아웃 필요 여부 확인
   */
  static shouldAutoLogout(): boolean {
    const settings = this.loadSecuritySettings();
    const inactiveMinutes = this.getInactiveMinutes();
    return inactiveMinutes >= settings.autoLogoutMinutes;
  }

  /**
   * 모든 인증 데이터 삭제
   */
  static clearAll(): void {
    try {
      Object.values(AUTH_STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.error('인증 데이터 전체 삭제 실패:', error);
    }
  }

  /**
   * 인증 상태 확인
   * [2026-01-05] 만료 시 자동 삭제하지 않음 - 갱신 시도 가능하도록
   */
  static isAuthenticated(): boolean {
    const token = this.loadToken();
    if (!token) {
      return false;
    }

    // 토큰 만료 확인 - 만료되어도 삭제하지 않음 (갱신 가능)
    if (token.isExpired()) {
      console.log('🔍 [Auth] 토큰 만료됨 - 갱신 필요');
      return false;
    }

    // 자동 로그아웃 확인 - 비활성 시간 초과 시에만 삭제
    if (this.shouldAutoLogout()) {
      console.log('🔍 [Auth] 비활성 시간 초과 - 로그아웃');
      this.clearAll();
      return false;
    }

    return true;
  }

  /**
   * 토큰 갱신 가능 여부 확인
   */
  static canRefresh(): boolean {
    const refreshToken = this.getRefreshToken();
    return !!refreshToken;
  }

  /**
   * 세션 유효성 검사
   */
  static validateSession(): {
    isValid: boolean;
    needsRefresh: boolean;
    shouldLogout: boolean;
    reason?: string;
  } {
    const token = this.loadToken();
    
    if (!token) {
      return {
        isValid: false,
        needsRefresh: false,
        shouldLogout: true,
        reason: '토큰이 없습니다',
      };
    }

    if (token.isExpired()) {
      return {
        isValid: false,
        needsRefresh: true,
        shouldLogout: false,
        reason: '토큰이 만료되었습니다',
      };
    }

    if (this.shouldAutoLogout()) {
      return {
        isValid: false,
        needsRefresh: false,
        shouldLogout: true,
        reason: '비활성으로 인한 자동 로그아웃',
      };
    }

    return {
      isValid: true,
      needsRefresh: token.needsRefresh(),
      shouldLogout: false,
    };
  }

  /**
   * 스토리지 용량 확인
   */
  static getStorageUsage(): {
    used: number;
    total: number;
    percentage: number;
  } {
    try {
      let used = 0;
      Object.values(AUTH_STORAGE_KEYS).forEach(key => {
        const value = localStorage.getItem(key);
        if (value) {
          used += value.length;
        }
      });

      // 대략적인 총 용량 (5MB)
      const total = 5 * 1024 * 1024;
      const percentage = (used / total) * 100;

      return { used, total, percentage };
    } catch (error) {
      console.error('스토리지 사용량 확인 실패:', error);
      return { used: 0, total: 0, percentage: 0 };
    }
  }
}